/**
 * @module app.fm
 */

import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { RouteConfig, NavigationInstruction } from 'aurelia-router';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { FaxService, IFaxSearchFilters } from 'app/fm/services/fax-service';
import { IQueueParameters } from 'app/core/models/essette-interfaces';
import { RouteDocumentAction } from 'app/fm/components/document-actions/route-document';
import { SplitDocumentAction } from 'app/fm/components/document-actions/split-document';
import { downloadFile, openFileInWindow } from 'app/shared/lib/files';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/essette-api';
import { guid } from 'app/shared/lib/guid';
import * as FM from 'app/fm/models/essette-api';

export interface IFaxDocumentActionsPageParameters extends Partial<IQueueParameters> {
    documentId: string
}

type DocumentActionSelection = 'route' | 'split' | 'attach';

type DocumentRotateAction = 'left' | '180' | 'right'

@autoinject()
export class FaxDocumentPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    private docId!: string;
    private faxDoc!: FM.FaxDocument;

    private queues: FM.FaxQueue[] = [];
    private priorities: FM.FaxPriority[] = [];

    private selectedAction: DocumentActionSelection = 'route';
    private canSplit: boolean = false;
    private isCheckedOut: boolean = false;

    private originalQueue: string = '';

    protected documentWindow: WindowProxy | null = null;

    ////****************** Element References ***************************************////

    public $routeDocumentAction!: RouteDocumentAction;
    public $splitDocumentAction!: SplitDocumentAction;

    protected id: guid = guid();

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(private readonly faxService: FaxService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected async canActivate({ documentId }: IFaxDocumentActionsPageParameters): Promise<boolean> {
        if(documentId) {
            this.docId = documentId;
            try {
                this.isCheckedOut = await this.checkoutDocument();
            }
            catch {
            }
            await this.reload();
            if(this.faxDoc) return true;
            else {
                this.toaster.warn().message(this.i18n.tr('fm:local.warning.documentCannotBeCheckedOut')).show();
            }
        }

        return false;
    }

    protected async activate(params: any, routeConfig: RouteConfig, navInstruction: NavigationInstruction): Promise<void> {
        if(this.faxDoc && routeConfig.navModel) {
            const title = this.i18n.tr('fm:routes.document.$title', { what: this.faxDoc.document?.file?.name });
            routeConfig.navModel.setTitle(title);
        }
        this.loadLookups();



        this.openDocument();
    }

    ////****************** Events ***************************************************////

    // Rotate selected document in server and open document in new window
    protected async rotateDocument(action: DocumentRotateAction): Promise<void> {
        if(!this.faxDoc) return;

        // Document will be rotated clockwise
        let degreesToRotate: number = 0;
        switch(action) {
            case 'left':
                degreesToRotate = 270;
                break;
            case 'right':
                degreesToRotate = 90;
                break;
            case '180':
                degreesToRotate = 180;
                break;
        }

        const rotateSuccessful = await this.rotateAndUpdateDocument(this.faxDoc, degreesToRotate)

        if(rotateSuccessful) {
            this.openDocument();
        }
    }


    ////****************** Functions ************************************************////

    protected async reload(): Promise<void> {
        if(this.isCheckedOut) {
            await this.loadFaxDocument();
            this.originalQueue = this.faxDoc.queue?.id || '';
            this.canSplit = this.faxDoc.details?.pageCount !== 1;
        }

        if(this.$routeDocumentAction) {
            this.$routeDocumentAction.resetForm();
        }
        if(this.$splitDocumentAction) {
            this.$splitDocumentAction.resetForm();
        }
    }

    protected async returnToQueue(): Promise<void> {
        const queueTarget = `#/fm/queues/fax/${this.originalQueue}`;

        if(this.faxDoc.metadata?.locked?.byUser?.id === this.env.user.id || !this.faxDoc.metadata?.locked?.byUser?.id) {
            await this.checkinDocument();
        }
        this.router.navigate(queueTarget);
    }

    protected openDocument(): void {
        if(!this.faxDoc) return;

        const extension = this.faxDoc.document?.mediaType?.fileExtension;
        if(extension && this.shouldView(extension)) {
            if(!this.documentWindow || this.documentWindow.closed) {
                this.documentWindow = window.open("", "", 'top=25, left=' + (screen.width / 4) + ',width=1000,height=' + (screen.height - 150));
            }

            if(this.documentWindow) {
                this.viewDocument(this.faxDoc, this.documentWindow);
                this.documentWindow.focus();
                return;
            }
        }

        this.downloadDocument(this.faxDoc);
    }

    protected shouldView(extension: string): boolean {
        // TODO: this might warrant being configurable in the future
        const allowed = ['.pdf', '.jpg', '.jpeg', '.png', '.txt'];
        return allowed.indexOf(extension) >= 0;
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    protected async checkoutDocument(): Promise<boolean> {
        const response = await this.faxService.checkOutFaxDocument(this.docId);
        return response.isSuccessful;
    }

    @apiCall
    protected async checkinDocument(): Promise<void> {
        await this.faxService.checkInFaxDocument(this.docId);
    }

    @apiCall
    protected async getNextDocument(): Promise<void> {
        try {
            const faxFilters: IFaxSearchFilters = {
                excludeCanceled: true,
                includeUnlocked: true,
                checkoutUserId: this.env.user.id
            };

            const faxesResponse = await this.faxService.loadFaxesByQueue(this.originalQueue, {}, [], faxFilters);
            await this.checkinDocument();

            // grab the next document checked out by user, or, if none found, the next unlocked document
            const next = faxesResponse.results.find(fax => fax.metadata?.locked?.byUser?.id === this.env.user.id) ||
                faxesResponse.results.find(fax => !fax.isLocked) || null;
            this.selectedAction = 'route';
            if(next?.id) {
                this.router.navigateToRoute('fm.document', { documentId: next.id });
            } else {
                return this.returnToQueue();
            }
        } catch(error) {
            throw error;
        }
    }

    @apiCall
    protected async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();
            const sorting = new Sorting();
            sorting.add({
                reference: 'description',
                direction: SortDirection.Ascending
            });
            const [priorityResponse, queueResponse] = await Promise.all([
                this.faxService.loadFaxPriorities(),
                this.faxService.loadFaxQueues({ sorting })
            ]);

            this.priorities = priorityResponse.results;
            this.queues = queueResponse.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async loadFaxDocument(): Promise<void> {
        try {
            this.state.beginLoad();
            const includes = ['document.mediaType', 'metadata.locked.byUser'];
            const response = await this.faxService.loadFaxDocument(this.docId, includes);
            this.faxDoc = response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    // #region Download/View Document
    @apiCall
    protected async downloadDocument(faxDocument: FM.FaxDocument): Promise<void> {
        if(!faxDocument.id) return;

        try {
            const blob = await this.faxService.downloadFaxFile(faxDocument.id);
            const fileName = faxDocument.document?.file?.name || 'download';

            downloadFile(blob, fileName);
        }
        catch(error) {
            this.toaster.error().message(this.i18n.tr('ui:messages.errors.download', { what: this.i18n.tr('fm:faxDocument.$short') })).show();
        }
    }

    @apiCall
    protected async viewDocument(faxDocument: FM.FaxDocument, childWindow: Window): Promise<void> {
        if(!faxDocument.id) return;

        try {
            const blob = await this.faxService.downloadFaxFile(faxDocument.id);
            openFileInWindow(childWindow, blob);
        }
        catch(error) {
            this.toaster.error().message(this.i18n.tr('ui:messages.errors.download', { what: this.i18n.tr('fm:faxDocument.$short') })).show();
            childWindow.close();
        }
    }
    // #endregion Download/View Document

    @apiCall
    protected async rotateAndUpdateDocument(faxDocument: FM.FaxDocument, degreesToRotate: number = 0): Promise<boolean> {
        if(!faxDocument.id) return false;

        let isSuccessful = false;

        try {
            this.state.beginSave();

            const rotateData = {
                details: {
                    alteredRotationDegrees: degreesToRotate
                }
            }

            const response = await this.faxService.rotateFaxDocument(faxDocument.id, rotateData);
            isSuccessful = response.isSuccessful;
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.saved('fm:faxDocument.$short', isSuccessful);
            this.state.endSave();
            return isSuccessful;
        }
    }
}
